import type {
  FetchBaseQueryError,
  RetryOptions,
} from "@reduxjs/toolkit/query/react"

type RetryConditionFunction = NonNullable<
  Extract<RetryOptions, { retryCondition?: unknown }>["retryCondition"]
>

export const getResponseCode = (
  error: FetchBaseQueryError | undefined,
): number | undefined => {
  if (!error) {
    return undefined
  }

  const status = error.status

  if (status === "PARSING_ERROR") {
    return error.originalStatus
  }

  if (typeof status != "number") {
    return undefined
  }

  return status
}

export const backoff = async (attempt: number, maxRetries: number) => {
  if (attempt === 1) {
    // No waiting for first retry to allow instant recovery after token refresh
    return
  }

  // Below is the defaultBackoff copied from RTK as it is not exported

  const attempts = Math.min(attempt, maxRetries) - 1

  const timeout = (Math.random() + 0.4) * (300 << attempts)
  await new Promise<void>((resolve) =>
    setTimeout(() => {
      resolve()
    }, timeout),
  )
}

export const retryCondition: RetryConditionFunction = (
  error,
  _args,
  { attempt },
) => {
  // bail out of re-tries for client-side errors like validation
  // Except unauth where we rely on automatic refresh and retry
  const responseCode = getResponseCode(error as FetchBaseQueryError | undefined)

  const shouldRetry =
    !responseCode ||
    responseCode === 401 ||
    responseCode < 400 ||
    responseCode >= 500

  return shouldRetry && attempt < 3
}
